import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { Link, useLocation, useOutletContext, useSearchParams } from "react-router-dom"
import { Menu } from "@headlessui/react"

import moment from "moment/moment"
import Delete from "pages/product/unit/delete"
import Filter from "pages/product/unit/filter"
import nProgress from "nprogress"
import { useAuth } from "contexts/auth-context"
import { Pagination } from "components"
import { lang } from "config"
import { ChevronDown, Pencil, Plus } from "components/icons/outline"
import { usePage } from "contexts/page-context"
import useProductUnitRepositories from "repositories/product-unit"

import { motion } from "framer-motion"
import { slideVariant, defaultVariant } from "components/transition"
import { PhotoOff } from "components/icons/outline"
import Table from "components/table"
import _ from "lodash"
import ImportLegacyMapping from "./import-legacy-mapping"

const SORT_OPTIONS = [
    { value: "name", label: "Product Unit" },
    { value: "product_model.name", label: "Product Model" },
    { value: "sku", label: "SKU" },
    { value: "created_at", label: "Dibuat Pada" },
    { value: "updated_at", label: "Diubah Pada" },
]

export default function List() {
    // Logged in user ability
    const { can, user } = useAuth()
    // Configuration
    const { configuration } = usePage()

    // Transition
    const { title, transition, setTransition } = useOutletContext()

    // React router location hook
    const location = useLocation()

    // Query parameters
    const [searchParams, setSearchParams] = useSearchParams()
    const [searchTemp, setSearchTemp] = useState(searchParams.get('search') || "")

    const searchInput = useRef()

    const page = searchParams.get('page')
    const search = searchParams.get('search')
    const name = searchParams.get('name')
    const from = searchParams.get('from')
    const to = searchParams.get('to')

    const orderBy = searchParams.get('order_by')
    const direction = searchParams.get('direction')
    const effectiveOrderBy = orderBy || 'name'
    const effectiveDirection = direction || 'asc'

    const productUnitParams = useMemo(() => ({
        page,
        search,
        name,
        from,
        to,

        order_by: effectiveOrderBy,
        direction: effectiveDirection
    }), [page, search, name, from, to, effectiveOrderBy, effectiveDirection])

    const { data: productUnitData, isLoading: isLoadingProductUnitData, mutate: mutateProductUnitData } = useProductUnitRepositories(productUnitParams)

    const [isLegacyMappingOpen, setIsLegacyMappingOpen] = useState(false)

    // Impor/Ekspor Harga dipindah ke menu Master Harga (200726). Di sini tinggal
    // Impor Legacy Mapping (bukan fitur harga).
    const canImportLegacyMapping = can('product-unit.update')
    const canShowImportMenu = canImportLegacyMapping
    const importMenuButtonClassName = "flex w-full items-center px-3 py-2.5 text-left text-xs font-semibold text-neutral-800 transition hover:bg-neutral-100"

    const mergeParams = useCallback((nextValues) => {
        const nextParams = new URLSearchParams(searchParams)

        Object.entries(nextValues).forEach(([key, value]) => {
            if (value === null || value === undefined || value === "") {
                nextParams.delete(key)
            } else {
                nextParams.set(key, value)
            }
        })

        setSearchParams(nextParams)
    }, [searchParams, setSearchParams])

    useEffect(() => {
        window.history.replaceState({}, document.title)

        if (transition.reverse) {
            setTransition({ type: 'fade', reverse: false })
        }
    }, [])

    useEffect(() => {
        searchInput.current.focus()
    }, [search])

    useEffect(() => {
        setSearchTemp(search || "")
    }, [search])

    const submitSearch = () => {
        if (searchTemp !== (search || "")) {
            mergeParams({ search: searchTemp, page: "1" })
        }
    }

    useEffect(() => {
        if (!searchParams.has("search") || search !== "") return

        const nextParams = new URLSearchParams(searchParams)
        nextParams.delete("search")
        setSearchParams(nextParams, { replace: true })
    }, [search, searchParams, setSearchParams])

    // Watch changes on petty cash data and loading state
    useEffect(() => {
        if (isLoadingProductUnitData) {
            nProgress.start()
        } else {
            nProgress.done()
        }
    }, [productUnitData, isLoadingProductUnitData])

    // Data filter handlers
    const updateFilter = (value) => {
        setSearchParams(value)
    }

    const removeFilter = () => {
        setSearchParams({})
    }

    // Data sort handlers
    const toggleSort = (value) => {
        if ((direction === null || direction === 'desc') || orderBy !== value) {
            mergeParams({
                order_by: value,
                direction: 'asc',
                page: "1"
            })
        } else {
            mergeParams({
                order_by: value,
                direction: 'desc',
                page: "1"
            })
        }
    }

    // Data list props
    const dataListHeaders = [
        {
            isAction: false,
            content: "Product Unit",
            sortKey: "name"
        },
        ...configuration('hierarchy').find((row) => row.key === 'product-model').value ? [{
            isAction: false,
            content: "Product Model",
            sortKey: "product_model.name"
        }] : [],
        {
            isAction: false,
            content: "SKU",
            sortKey: "sku"
        },
        {
            isAction: false,
            content: lang.created_at,
            sortKey: "created_at"
        },
        {
            isAction: false,
            content: lang.updated_at,
            sortKey: "updated_at"
        },
        {
            isAction: true,
            content: null
        }
    ]

    const dataListColumns = [
        {
            type: "default",
            bold: false,
            content: (value) => (
                <div className="flex items-center">
                    {value.model_cover || value.image ? (
                        <a href={value.model_cover || value.image} target="_blank" className="w-10 h-10">
                            <img className="object-cover rounded-full aspect-square" src={value.model_cover || value.image} alt="Product Image" />
                        </a>
                    ) : (
                        <div className="flex items-center justify-center w-10 h-10 rounded-full bg-neutral-100">
                            <PhotoOff className="w-5 h-5" strokeWidth={1.5} />
                        </div>
                    )}
                    <div className="ml-4 space-y-1">
                        <div>{value.name}</div>
                        <div className="font-medium text-neutral-900">{value.long_name}</div>
                    </div>
                </div>
            )
        },
        ...configuration('hierarchy').find((row) => row.key === 'product-model').value ? [{
            type: "default",
            bold: false,
            content: (value) => (
                <div className="flex flex-col space-y-1">
                    <div>{value.product_model?.name}</div>
                    <div className="font-medium text-neutral-900">{value.product_model?.long_name}</div>
                </div>
            )
        }] : [],
        {
            type: "default",
            bold: false,
            content: (value) => value.sku
        },
        {
            type: "default",
            bold: false,
            content: (value) => moment(value.created_at).format('MMMM D, YYYY')
        },
        {
            type: "default",
            bold: false,
            content: (value) => moment(value.updated_at).format('MMMM D, YYYY')
        },
        {
            type: "action",
            content: (value) => (
                <>
                    {can('product-unit.update') && (
                        <Link to={`${value.id}/edit`} state={{ back: title, from: location?.pathname, transition: 'slide' }} className="inline-flex items-center p-1 text-white transition rounded-full bg-brand-800 active:hover:scale-90">
                            <Pencil className="w-6 h-6" />
                        </Link>
                    )}

                    {can('product-unit.delete') && (
                        <Delete data={value} onSuccess={() => mutateProductUnitData()} />
                    )}
                </>
            )
        }
    ]

    // Export data function
    // const exportData = (type) => {
    //     const timestamp = new Date().toISOString()

    //     toast.loading("Loading", {
    //         id: `toast-${timestamp}`
    //     })

    //     axios.post(`${process.env.REACT_APP_INVENTORY_API_URL}/product/unit/export`, {
    //         type,
    //         search,
    //         name,
    //         from,
    //         to,
    //         phone: user?.phone
    //     }).then((response) => {
    //         toast.success(response.data.message, {
    //             id: `toast-${timestamp}`
    //         })
    //     }).catch((error) => {
    //         toast.error(error.response.data.message, {
    //             id: `toast-${timestamp}`
    //         })
    //     })
    // }

    return (
        // <motion.div variants={defaultVariant} initial="initial" animate="animate" exit="exit" className="space-y-6">
        <motion.div className="space-y-6">
            <div className="flex items-center justify-between text-xs">
                <div className="flex items-center sm:space-x-2">
                    <div className="relative hidden sm:block">
                        <div className="absolute inset-y-0 flex items-center pl-3 pointer-events-none">
                            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" width={24} height={24} viewBox="0 0 24 24" strokeWidth={1} stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                                <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                                <circle cx={10} cy={10} r={7}></circle>
                                <line x1={21} y1={21} x2={15} y2={15}></line>
                            </svg>
                        </div>
                        <input ref={searchInput} onChange={(e) => setSearchTemp(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); submitSearch() } }} value={searchTemp} type="text" placeholder={`${lang.search} Product Unit`} autoComplete="off" className="w-64 py-3 pl-8 pr-4 text-xs transition border border-neutral-200 focus:outline-none rounded-xl focus:border-neutral-400 focus:ring focus:ring-neutral-200" />
                    </div>
                    <div className="hidden sm:block">
                        <select
                            value={effectiveOrderBy}
                            onChange={(e) => mergeParams({ order_by: e.target.value, direction: effectiveDirection })}
                            className="py-3 pl-3 pr-8 text-xs transition border border-neutral-200 focus:outline-none rounded-xl focus:border-neutral-400 focus:ring focus:ring-neutral-200"
                        >
                            {SORT_OPTIONS.map((option) => (
                                <option key={option.value} value={option.value}>{option.label}</option>
                            ))}
                        </select>
                    </div>
                    <div className="hidden sm:block">
                        <select
                            value={effectiveDirection}
                            onChange={(e) => mergeParams({ order_by: effectiveOrderBy, direction: e.target.value })}
                            className="py-3 pl-3 pr-8 text-xs transition border border-neutral-200 focus:outline-none rounded-xl focus:border-neutral-400 focus:ring focus:ring-neutral-200"
                        >
                            <option value="asc">Asc</option>
                            <option value="desc">Desc</option>
                        </select>
                    </div>
                    <Filter onSubmit={updateFilter} onRemove={removeFilter} data={Object.fromEntries(Object.entries({ name, from, to }).filter(([_, v]) => v != null))} />
                </div>
                <div className="flex items-center space-x-2">
                    {canShowImportMenu && (
                        <Menu as="div" className="relative inline-block text-left">
                            {({ close }) => (
                                <>
                                    <Menu.Button className="inline-flex items-center px-4 py-3 space-x-2 text-xs font-semibold transition border border-neutral-200 bg-white text-neutral-800 rounded-xl hover:bg-neutral-50 focus:outline-none focus:ring-2 focus:ring-neutral-200">
                                        <span>Impor</span>
                                        <ChevronDown className="w-4 h-4 text-neutral-500" strokeWidth={1.5} />
                                    </Menu.Button>
                                    <Menu.Items className="absolute right-0 z-30 w-64 mt-2 overflow-hidden bg-white border shadow-xl rounded-xl border-neutral-200 ring-1 ring-black/5 focus:outline-none">
                                        <div className="px-3 py-2 text-[10px] font-semibold uppercase tracking-wide text-neutral-400 bg-neutral-50">
                                            Product Unit
                                        </div>
                                        <div className="p-1">
                                            {canImportLegacyMapping && (
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        close()
                                                        setIsLegacyMappingOpen(true)
                                                    }}
                                                    className={importMenuButtonClassName}
                                                >
                                                    <span>Impor Legacy Mapping</span>
                                                </button>
                                            )}
                                        </div>
                                    </Menu.Items>
                                </>
                            )}
                        </Menu>
                    )}
                    {can("product-unit.create") && (
                        <div>
                        <Link
                            to="create"
                            onClick={() => {
                            setTransition({ type: "slide", reverse: true })
                            }}
                            state={{
                            back: title,
                            from: location?.pathname,
                            transition: { type: "slide", reverse: true },
                            }}
                            className="inline-flex items-center px-4 py-3 space-x-2 text-white transition bg-brand-800 rounded-xl active:hover:scale-90"
                        >
                            <Plus className="w-4 h-4" stroke={1.5} />
                            <span>{lang.create}</span>
                        </Link>
                        </div>
                    )}
                    </div>
            </div>
            <Table isLoading={isLoadingProductUnitData} data={productUnitData?.data ?? []} headers={dataListHeaders} columns={dataListColumns} key={(value) => value.id} isEmpty={productUnitData?.data.length === 0 && !search && !isLoadingProductUnitData} isEmptyOnSearching={productUnitData?.data.length === 0 && search && !isLoadingProductUnitData} toggleSort={toggleSort} orderBy={effectiveOrderBy} direction={effectiveDirection} />
            <Pagination links={productUnitData?.links} from={productUnitData?.from} to={productUnitData?.to} total={productUnitData?.total} />
            {canImportLegacyMapping && (
                <ImportLegacyMapping
                    isOpen={isLegacyMappingOpen}
                    onClose={() => setIsLegacyMappingOpen(false)}
                    onSuccess={() => mutateProductUnitData()}
                    userName={user?.name || user?.username || user?.email}
                />
            )}
        </motion.div>
    )
}
